import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Building2, ChevronRight } from 'lucide-react';
import { TierBadge } from '@/components/bloodwork/TierBadge';

interface TimelineUpload {
  id: string;
  lab_date: string;
  lab_provider: string | null;
  status: string;
  tier: 1 | 2 | 3 | null;
  summary: string | null;
}

interface UploadTimelineProps {
  uploads: TimelineUpload[];
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const item = {
  hidden: { opacity: 0, x: -12 },
  show: { opacity: 1, x: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function truncate(text: string | null, max: number): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
}

export function UploadTimeline({ uploads }: UploadTimelineProps) {
  if (uploads.length === 0) return null;

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-navy">Upload History</h2>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative"
      >
        {/* Timeline line */}
        <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" />

        <div className="space-y-3">
          {uploads.map((upload) => (
            <motion.div key={upload.id} variants={item}>
              <Link
                to={`/results/${upload.id}`}
                className="group relative flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-copper/30 hover:shadow-md"
              >
                {/* Timeline dot */}
                <div className="relative z-10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-copper/10 group-hover:bg-copper/20 transition-colors">
                  <div className="h-2.5 w-2.5 rounded-full bg-copper" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-navy">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      {formatDate(upload.lab_date)}
                    </span>
                    {upload.lab_provider && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <Building2 className="h-3 w-3" />
                        {upload.lab_provider}
                      </span>
                    )}
                    {upload.tier && <TierBadge tier={upload.tier} size="sm" />}
                  </div>
                  {upload.summary && (
                    <p className="mt-1.5 text-sm text-gray-500 leading-relaxed">
                      {truncate(upload.summary, 100)}
                    </p>
                  )}
                </div>

                {/* Arrow */}
                <ChevronRight className="h-5 w-5 shrink-0 text-gray-300 group-hover:text-copper transition-colors mt-0.5" />
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
